import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  StyledTable,
  StyledTableRow,
  StyledTableData,
  StyledTableHeaderCellContainer,
  StyledFooterContainer,
  StyledColGroup,
  StyledTableContainer,
  StyledEmptyContainer,
  StyledExpandIcon,
  StyledExpandedCell,
  StyledExpandedCellContent,
  StyledTableHeader,
  StyledScrollableTableWrapper,
  StyledActionIconContainer,
  StyledHeaderScrollWrapper,
} from "./Table.styled";
import { Loader } from "../Loader/Loader";
import { Pagination, PaginationProps } from "../Pagination/Pagination";
import { IconManager } from "../IconManager/IconManager";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { ReactComponent as ChevronDownIcon } from "../../../icons/chevronDown.svg";
import { ReactComponent as ChevronRightIcon } from "../../../icons/chevronRight.svg";
import { ReactComponent as EditIcon } from "../../../icons/pencil.svg";
import { ReactComponent as DeleteIcon } from "../../../icons/bin.svg";
import { MessageBox } from "../../MessageBox/MessageBox";
import {
  EMPTY_STRING,
  NO_RESULT_FOUND_LABEL,
} from "../../../constants/Dictionary";
import { theme as style } from "../../../constants/StyledComponents";
import { BodyTyp } from "../../Typography/Typography";
import { ReactComponent as DetachIcon } from "../../../icons/detach.svg";
import { Checkbox } from "../Checkbox/Checkbox";
import { Tooltip, TooltipVariantEnum } from "../Tooltip/Tooltip";

const TruncatedCell = ({
  children,
  text,
  columnWidth,
}: {
  children: React.ReactNode;
  text: string;
  columnWidth?: number;
}) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const maxWidth = columnWidth ? columnWidth : undefined;

  useEffect(() => {
    const el = measureRef.current;
    if (el && maxWidth !== undefined) {
      setIsTruncated(el.offsetWidth > maxWidth);
    }
  });

  if (!maxWidth) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      message={text}
      hide={!isTruncated}
      variant={TooltipVariantEnum.DARK}
    >
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth,
        }}
      >
        {/* Hidden span outside overflow:hidden to measure the true text width */}
        <span
          ref={measureRef}
          style={{
            position: "fixed",
            visibility: "hidden",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
        {children}
      </div>
    </Tooltip>
  );
};

export enum TableThemeEnum {
  DARK = "dark",
  LIGHT = "light",
}

export enum TableCellAlignEnum {
  LEFT = "left",
  RIGHT = "right",
  CENTER = "center",
}

export enum TableFixedCellAlignEnum {
  LEFT = "left",
  RIGHT = "right",
}

interface TableHeaderBase<T> {
  title?: string | React.ReactNode;
  key: string;
  render?: (row: T) => React.ReactNode;
  getCellText?: (row: T) => string;
  align?: TableCellAlignEnum;
  cellAlign?: TableCellAlignEnum;
  width?: string | number;
  fixed?: TableFixedCellAlignEnum;
}

interface Expandable<T> {
  expandedRowRender: (record: T) => React.ReactNode;
  rowExpandable?: (record: T) => boolean;
}

export interface TableProps<T extends object> {
  headers: TableHeaderBase<T>[];
  data: T[];
  rowKey?: keyof T | ((record: T) => string);
  pagination?: PaginationProps;
  loading?: boolean;
  onRow?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  rowHasDelete?: (row: T) => boolean;
  rowHasEdit?: (row: T) => boolean;
  emptyText?: string;
  theme?: TableThemeEnum;
  expandable?: Expandable<T>;
  clickableRow?: (row: T) => boolean;
  scrollY?: string | number;
  onDetach?: (row: T) => void;
  onCheckbox?: (row: T, value: boolean) => void;
  onSelectAll?: (rows: T[], value: boolean) => void;
  selectedRows?: T[];
  isCheckboxDisabled?: (row: T) => boolean;
  showRowAction?: boolean;
}

const MIN_COLUMN_WIDTH = 160;

export const Table = <T extends object>({
  headers,
  data,
  rowKey,
  pagination,
  loading,
  onRow,
  onEdit,
  onDelete,
  rowHasDelete,
  rowHasEdit,
  emptyText,
  theme = TableThemeEnum.LIGHT,
  expandable,
  clickableRow,
  scrollY,
  onDetach,
  onCheckbox,
  selectedRows,
  onSelectAll,
  isCheckboxDisabled,
  showRowAction,
}: TableProps<T>) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLTableElement>(null);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  const selectableRows = useMemo(
    () => data.filter((row) => !isCheckboxDisabled?.(row)),
    [data, isCheckboxDisabled],
  );

  useEffect(() => {
    const body = scrollableRef.current;
    const header = headerRef.current;
    if (!body || !header) return;

    const onBodyScroll = () => {
      header.scrollLeft = body.scrollLeft;
    };

    const onHeaderScroll = () => {
      body.scrollLeft = header.scrollLeft;
    };

    body.addEventListener("scroll", onBodyScroll);
    header.addEventListener("scroll", onHeaderScroll);

    return () => {
      body.removeEventListener("scroll", onBodyScroll);
      header.removeEventListener("scroll", onHeaderScroll);
    };
  }, []);

  useLayoutEffect(() => {
    if (!tableBodyRef.current || !scrollableRef.current) return;

    const syncAll = () => {
      if (tableBodyRef.current) {
        const rows = Array.from(
          tableBodyRef.current.querySelectorAll(
            "tbody tr:not(.row-gap):not([style*='padding: 0'])",
          ),
        );
        const firstValidRow = rows.find((row) => {
          const cells = Array.from(row.querySelectorAll("td"));
          return (
            cells.length > 0 && cells.every((cell) => cell.offsetWidth > 0)
          );
        });
        if (firstValidRow) {
          const cells = Array.from(firstValidRow.querySelectorAll("td"));
          setColumnWidths(
            cells.map((cell) => cell.getBoundingClientRect().width),
          );
        }
      }
      if (scrollableRef.current) {
        setContainerWidth(scrollableRef.current.clientWidth);
        setIsOverflowing(
          scrollableRef.current.scrollWidth > scrollableRef.current.clientWidth,
        );
      }
    };

    let rafId: number;
    const debouncedSync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncAll);
    };

    debouncedSync();
    const observer = new ResizeObserver(debouncedSync);
    observer.observe(tableBodyRef.current);
    observer.observe(scrollableRef.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [data, loading]);

  const hasClickableRows = useMemo(() => {
    if (!onRow) return false;
    if (!clickableRow) return true;
    return data.some((row) => clickableRow(row));
  }, [onRow, clickableRow, data]);

  const activeActionsCount = useMemo(
    () =>
      [!!onDetach, !!onEdit, !!onDelete, hasClickableRows, !!expandable].filter(
        Boolean,
      ).length,
    [onDetach, onEdit, onDelete, hasClickableRows, expandable],
  );

  const leftFixedOffsets = useMemo(() => {
    if (columnWidths.length === 0) return [];
    const checkboxOffset = onCheckbox ? 1 : 0;
    return headers.map((_, upToIndex) => {
      let sum = onCheckbox ? (columnWidths[0] ?? 0) : 0;
      for (let i = 0; i < upToIndex; i++) {
        if (headers[i].fixed === TableFixedCellAlignEnum.LEFT) {
          sum += columnWidths[checkboxOffset + i] ?? 0;
        }
      }
      return sum;
    });
  }, [columnWidths, headers, onCheckbox]);

  const rightFixedOffsets = useMemo(() => {
    if (columnWidths.length === 0) return [];
    const checkboxOffset = onCheckbox ? 1 : 0;
    const totalCols = columnWidths.length;
    return headers.map((_, afterIndex) => {
      let sum = 0;
      for (let i = 0; i < activeActionsCount; i++) {
        sum += columnWidths[totalCols - activeActionsCount + i] ?? 0;
      }
      for (let i = afterIndex + 1; i < headers.length; i++) {
        if (headers[i].fixed === TableFixedCellAlignEnum.RIGHT) {
          sum += columnWidths[checkboxOffset + i] ?? 0;
        }
      }
      return sum;
    });
  }, [columnWidths, headers, onCheckbox, activeActionsCount]);

  const getRowKey = (record: T, index: number): string => {
    if (!rowKey) return index.toString();
    if (typeof rowKey === "function") {
      return rowKey(record);
    }
    const value = record[rowKey];
    return typeof value === "string" || typeof value === "number"
      ? value.toString()
      : index.toString();
  };

  const toggleRowExpansion = (row: T, rowIndex: number) => {
    const key = getRowKey(row, rowIndex);
    setExpandedRows((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
    );
  };

  const isRowExpanded = (row: T, rowIndex: number) => {
    return expandedRows.includes(getRowKey(row, rowIndex));
  };

  const isRowExpandable = (row: T) => {
    if (!expandable) return false;
    return expandable.rowExpandable ? expandable.rowExpandable(row) : true;
  };

  const selectedKeys = useMemo(
    () => new Set(selectedRows?.map((r) => getRowKey(r, 0)) ?? []),
    [selectedRows, rowKey],
  );

  const isRowSelected = (row: T) => selectedKeys.has(getRowKey(row, 0));

  const fixedRightOffsets = useMemo(() => {
    if (columnWidths.length === 0) return [];
    const totalCols = columnWidths.length;
    return Array.from({ length: activeActionsCount }, (_, i) => {
      let sum = 0;
      for (let j = i + 1; j < activeActionsCount; j++) {
        sum += columnWidths[totalCols - activeActionsCount + j];
      }
      return sum;
    });
  }, [columnWidths, activeActionsCount]);

  const checkboxCell = (row: T, rowSelected: boolean) => {
    if (!onCheckbox) return null;
    const disabled = isCheckboxDisabled?.(row) ?? false;
    return (
      <StyledTableData
        $theme={theme}
        $align={TableCellAlignEnum.LEFT}
        $selected={rowSelected}
        $fixed={TableFixedCellAlignEnum.LEFT}
        $fixedShift={0}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          value={rowSelected}
          disabled={disabled}
          onChange={(newValue) => onCheckbox(row, newValue)}
        />
      </StyledTableData>
    );
  };

  const renderActionCell = (
    icon: React.ReactNode,
    onClick: ((e: React.MouseEvent) => void) | (() => void) | null,
    row: T,
    rowIndex: number,
    actionIndex: number,
    showRightGap: boolean,
    options?: {
      showAction?: boolean;
      isExpandIcon?: boolean;
      expanded?: boolean;
    },
    id?: string,
  ) => (
    <StyledTableData
      $theme={theme}
      $align={TableCellAlignEnum.RIGHT}
      $selected={isRowSelected(row)}
      $fixed={TableFixedCellAlignEnum.RIGHT}
      $fixedShift={fixedRightOffsets[actionIndex] ?? 0}
      $showRightGap={isOverflowing && showRightGap}
    >
      <StyledActionIconContainer $showAction={options?.showAction}>
        {options?.isExpandIcon ? (
          <StyledExpandIcon $expanded={options.expanded ?? false}>
            <IconManager
              size={IconSizeEnum.MEDIUM}
              onClick={onClick ?? undefined}
              id="expand-icon"
            >
              {icon}
            </IconManager>
          </StyledExpandIcon>
        ) : (
          onClick && (
            <IconManager size={IconSizeEnum.MEDIUM} onClick={onClick} id={id}>
              {icon}
            </IconManager>
          )
        )}
      </StyledActionIconContainer>
    </StyledTableData>
  );

  const expandCell = (
    row: T,
    rowIndex: number,
    actionIndex: number,
    showRightGap: boolean,
  ) =>
    isRowExpandable(row) ? (
      renderActionCell(
        <ChevronDownIcon />,
        (e: React.MouseEvent) => {
          e.stopPropagation();
          toggleRowExpansion(row, rowIndex);
        },
        row,
        rowIndex,
        actionIndex,
        showRightGap,
        { isExpandIcon: true, expanded: isRowExpanded(row, rowIndex) },
        "expand-icon",
      )
    ) : (
      <StyledTableData
        $theme={theme}
        $align={TableCellAlignEnum.RIGHT}
        $selected={isRowSelected(row)}
        $fixed={TableFixedCellAlignEnum.RIGHT}
        $fixedShift={fixedRightOffsets[actionIndex] ?? 0}
        $showRightGap={isOverflowing && showRightGap}
      />
    );

  const tableActionsConfig = [
    {
      condition: !!onDetach,
      component: (row: T, rowIndex: number, i: number, gap: boolean) =>
        renderActionCell(
          <DetachIcon />,
          () => onDetach!(row),
          row,
          rowIndex,
          i,
          gap,
          undefined,
          "detach-icon",
        ),
    },
    {
      condition: !!onEdit,
      component: (row: T, rowIndex: number, i: number, gap: boolean) => {
        const show = !rowHasEdit || rowHasEdit(row);
        return renderActionCell(
          show ? <EditIcon /> : null,
          show ? () => onEdit!(row) : null,
          row,
          rowIndex,
          i,
          gap,
          { showAction: show },
          "edit-icon",
        );
      },
    },
    {
      condition: !!onDelete,
      component: (row: T, rowIndex: number, i: number, gap: boolean) => {
        const show = !rowHasDelete || rowHasDelete(row);
        return renderActionCell(
          show ? <DeleteIcon /> : null,
          show ? () => onDelete!(row) : null,
          row,
          rowIndex,
          i,
          gap,
          { showAction: show },
          "delete-icon",
        );
      },
    },
    {
      condition: hasClickableRows,
      component: (row: T, rowIndex: number, i: number, gap: boolean) =>
        onRow &&
        (clickableRow ? clickableRow(row) : true) &&
        showRowAction !== false ? (
          renderActionCell(
            <ChevronRightIcon />,
            () => onRow(row),
            row,
            rowIndex,
            i,
            gap,
            undefined,
            "navigate-icon",
          )
        ) : (
          <StyledTableData
            $theme={theme}
            $align={TableCellAlignEnum.RIGHT}
            $selected={isRowSelected(row)}
            $fixed={TableFixedCellAlignEnum.RIGHT}
            $fixedShift={fixedRightOffsets[i] ?? 0}
            $showRightGap={isOverflowing && gap}
          />
        ),
    },
    { condition: !!expandable, component: expandCell },
  ];

  const activeActions = tableActionsConfig.filter((config) => config.condition);

  const totalColspan =
    (onCheckbox ? 1 : 0) + headers.length + activeActionsCount;

  const totalColumnWidth = useMemo(() => {
    if (tableBodyRef.current) {
      return tableBodyRef.current.scrollWidth;
    }
    return Math.ceil(columnWidths.reduce((sum, w) => sum + w, 0));
  }, [columnWidths]);

  const firstRightFixedHeaderIndex = useMemo(
    () => headers.findIndex((h) => h.fixed === TableFixedCellAlignEnum.RIGHT),
    [headers],
  );

  const lastLeftFixedHeaderIndex = useMemo(
    () =>
      headers
        .map((h, i) => (h.fixed === TableFixedCellAlignEnum.LEFT ? i : -1))
        .filter((i) => i !== -1)
        .pop() ?? -1,
    [headers],
  );

  const isEmpty = !loading && data.length === 0;

  return (
    <Loader loading={loading ?? false}>
      <StyledTableContainer $hasPagination={!!pagination}>
        <StyledHeaderScrollWrapper ref={headerRef}>
          <StyledTable
            style={
              totalColumnWidth
                ? {
                    width: `${totalColumnWidth}px`,
                    minWidth: `${totalColumnWidth}px`,
                  }
                : undefined
            }
          >
            <colgroup>
              {columnWidths.map((width, i) => (
                <col
                  key={i}
                  style={{ width: `${width}px`, minWidth: `${width}px` }}
                />
              ))}
            </colgroup>
            <thead>
              <StyledTableRow>
                {onCheckbox && (
                  <StyledTableHeader
                    $align={TableCellAlignEnum.CENTER}
                    $fixed={TableFixedCellAlignEnum.LEFT}
                    $fixedShift={0}
                    $theme={theme}
                  >
                    <Checkbox
                      value={
                        selectableRows.length > 0 &&
                        selectableRows.every((row) =>
                          selectedKeys.has(getRowKey(row, 0)),
                        )
                      }
                      onChange={(checked) =>
                        onSelectAll?.(selectableRows, checked)
                      }
                      disabled={selectableRows.length === 0}
                    />
                  </StyledTableHeader>
                )}
                {headers.map((header, index) => {
                  const colIndex = (onCheckbox ? 1 : 0) + index;
                  return (
                    <StyledTableHeader
                      key={`${header.key}-${index}`}
                      $align={header.align}
                      $fixed={header.fixed}
                      {...(header.fixed === TableFixedCellAlignEnum.LEFT && {
                        $fixedShift: leftFixedOffsets[index] ?? 0,
                      })}
                      {...(header.fixed === TableFixedCellAlignEnum.RIGHT && {
                        $fixedShift: rightFixedOffsets[index] ?? 0,
                      })}
                      $theme={theme}
                      style={
                        columnWidths[colIndex]
                          ? {
                              width: `${columnWidths[colIndex]}px`,
                              minWidth: `${columnWidths[colIndex]}px`,
                              maxWidth: `${columnWidths[colIndex]}px`,
                            }
                          : undefined
                      }
                    >
                      <StyledTableHeaderCellContainer $align={header.align}>
                        <BodyTyp
                          variant="2"
                          fontWeight={FontWeightEnum.SEMIBOLD}
                        >
                          {header.title || ""}
                        </BodyTyp>
                      </StyledTableHeaderCellContainer>
                    </StyledTableHeader>
                  );
                })}
                {activeActions.map((_, index) => {
                  const colIndex =
                    (onCheckbox ? 1 : 0) + headers.length + index;

                  return (
                    <StyledTableHeader
                      key={`action-header-${index}`}
                      $theme={theme}
                      $fixed={TableFixedCellAlignEnum.RIGHT}
                      $fixedShift={fixedRightOffsets[index] ?? 0}
                      style={
                        columnWidths[colIndex]
                          ? {
                              width: `${columnWidths[colIndex]}px`,
                              minWidth: `${columnWidths[colIndex]}px`,
                              maxWidth: `${columnWidths[colIndex]}px`,
                            }
                          : undefined
                      }
                    />
                  );
                })}
              </StyledTableRow>
            </thead>
          </StyledTable>
        </StyledHeaderScrollWrapper>
        <StyledScrollableTableWrapper
          $height={scrollY || "auto"}
          $theme={theme}
          ref={scrollableRef}
        >
          <StyledTable ref={tableBodyRef}>
            <StyledColGroup
              $cols={[
                ...(onCheckbox
                  ? [
                      {
                        width: style.variables.table_icon_cell_width,
                        minWidth: style.variables.table_icon_cell_width,
                      },
                    ]
                  : []),
                ...headers.map((h) =>
                  h.width
                    ? { width: h.width, minWidth: h.width }
                    : { minWidth: MIN_COLUMN_WIDTH },
                ),
                ...activeActions.map(() => ({
                  width: style.variables.table_icon_cell_width,
                  minWidth: style.variables.table_icon_cell_width,
                })),
              ]}
            >
              {onCheckbox && <col key="checkbox-col" />}
              {headers.map((_, index) => (
                <col key={index} />
              ))}
              {activeActions.map((_, index) => (
                <col key={`action-${index}`} />
              ))}
            </StyledColGroup>

            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={totalColspan}>
                    <StyledEmptyContainer>
                      <MessageBox
                        message={emptyText ?? NO_RESULT_FOUND_LABEL}
                      />
                    </StyledEmptyContainer>
                  </td>
                </tr>
              ) : (
                data.map((row, rowIndex) => {
                  const rowSelected = isRowSelected(row);

                  return (
                    <React.Fragment key={getRowKey(row, rowIndex)}>
                      <StyledTableRow
                        $clickable={
                          onCheckbox
                            ? !isCheckboxDisabled?.(row)
                            : onRow && (clickableRow ? clickableRow(row) : true)
                        }
                        onClick={() => {
                          if (onCheckbox) {
                            if (isCheckboxDisabled?.(row)) return;
                            onCheckbox(row, !rowSelected);
                            return;
                          }
                          if (
                            onRow &&
                            (clickableRow ? clickableRow(row) : true)
                          ) {
                            onRow(row);
                          }
                        }}
                        $expanded={isRowExpanded(row, rowIndex)}
                      >
                        {onCheckbox && checkboxCell(row, rowSelected)}
                        {headers.map((header, cellIndex) => {
                          const isLastLeftFixed =
                            header.fixed === TableFixedCellAlignEnum.LEFT &&
                            cellIndex === lastLeftFixedHeaderIndex;
                          const showRightGap =
                            header.fixed === TableFixedCellAlignEnum.RIGHT &&
                            cellIndex === firstRightFixedHeaderIndex;

                          return (
                            <StyledTableData
                              key={cellIndex}
                              $align={header.cellAlign}
                              $theme={theme}
                              $selected={rowSelected}
                              $fixed={header.fixed}
                              {...(header.fixed ===
                                TableFixedCellAlignEnum.LEFT && {
                                $fixedShift: leftFixedOffsets[cellIndex] ?? 0,
                              })}
                              {...(header.fixed ===
                                TableFixedCellAlignEnum.RIGHT && {
                                $fixedShift: rightFixedOffsets[cellIndex] ?? 0,
                              })}
                              $widthString={header.width}
                              $showLeftGap={isOverflowing && isLastLeftFixed}
                              $showRightGap={isOverflowing && showRightGap}
                            >
                              {header.render ? (
                                header.getCellText ? (
                                  <TruncatedCell
                                    text={header.getCellText(row)}
                                    columnWidth={
                                      columnWidths[
                                        (onCheckbox ? 1 : 0) + cellIndex
                                      ]
                                    }
                                  >
                                    {header.render(row)}
                                  </TruncatedCell>
                                ) : (
                                  header.render(row)
                                )
                              ) : (
                                <TruncatedCell
                                  text={
                                    row[header.key as keyof T]?.toString() ??
                                    EMPTY_STRING
                                  }
                                  columnWidth={
                                    columnWidths[
                                      (onCheckbox ? 1 : 0) + cellIndex
                                    ]
                                  }
                                >
                                  <BodyTyp
                                    variant="2"
                                    fontWeight={FontWeightEnum.REGULAR}
                                    {...(typeof header.width === "number" && {
                                      maxWidth: header.width,
                                    })}
                                  >
                                    {row[header.key as keyof T]?.toString() ??
                                      EMPTY_STRING}
                                  </BodyTyp>
                                </TruncatedCell>
                              )}
                            </StyledTableData>
                          );
                        })}
                        {activeActions
                          .filter((_, index) => {
                            if (!onCheckbox) return true;
                            return (
                              index === activeActions.length - 1 && !!expandable
                            );
                          })
                          .map((action, index) => {
                            const showRightGap =
                              index === 0 && firstRightFixedHeaderIndex === -1;

                            return (
                              <React.Fragment key={`action-${index}`}>
                                {action.component(
                                  row,
                                  rowIndex,
                                  index,
                                  showRightGap,
                                )}
                              </React.Fragment>
                            );
                          })}
                      </StyledTableRow>
                      {expandable && isRowExpanded(row, rowIndex) && (
                        <tr>
                          <td colSpan={totalColspan} style={{ padding: 0 }}>
                            <StyledExpandedCell
                              $theme={theme}
                              $selected={rowSelected}
                            >
                              <StyledExpandedCellContent
                                $width={containerWidth ? containerWidth : null}
                                $theme={theme}
                                $selectedRow={rowSelected}
                              >
                                {expandable.expandedRowRender(row)}
                              </StyledExpandedCellContent>
                            </StyledExpandedCell>
                          </td>
                        </tr>
                      )}
                      <tr className="row-gap">
                        <td colSpan={totalColspan} />
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </StyledTable>
        </StyledScrollableTableWrapper>
        {pagination && (
          <StyledFooterContainer>
            <Pagination
              current={pagination.current}
              total={pagination.total}
              onChange={pagination.onChange}
              pageSize={pagination.pageSize}
            />
          </StyledFooterContainer>
        )}
      </StyledTableContainer>
    </Loader>
  );
};
